var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { BaseSelect } from 'rc-select';
import { conductCheck } from 'rc-tree/lib/utils/conductUtil';
import useId from 'rc-select/lib/hooks/useId';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import OptionList from './OptionList';
import TreeNode from './TreeNode';
import { formatStrategyValues, SHOW_ALL, SHOW_PARENT, SHOW_CHILD } from './utils/strategyUtil';
import TreeSelectContext from './TreeSelectContext';
import LegacyContext from './LegacyContext';
import useTreeData from './hooks/useTreeData';
import { toArray, fillFieldNames, isNil } from './utils/valueUtil';
import useCache from './hooks/useCache';
import useRefFunc from './hooks/useRefFunc';
import useDataEntities from './hooks/useDataEntities';
import { fillAdditionalInfo, fillLegacyProps } from './utils/legacyUtil';
import useCheckedKeys from './hooks/useCheckedKeys';
import useFilterTreeData from './hooks/useFilterTreeData';
import warningProps from './utils/warningPropsUtil';
import warning from 'rc-util/lib/warning';
function isRawValue(value) {
    return !value || typeof value !== 'object';
}
const TreeSelect = React.forwardRef((props, ref) => {
    const { id, prefixCls = 'rc-tree-select', 
    // Value
    value, defaultValue, onChange, onSelect, onDeselect, 
    // Search
    searchValue, inputValue, onSearch, autoClearSearchValue = true, filterTreeNode, treeNodeFilterProp = 'value', 
    // Selector
    showCheckedStrategy = SHOW_CHILD, treeNodeLabelProp, 
    //  Mode
    multiple, treeCheckable, treeCheckStrictly, labelInValue, 
    // FieldNames
    fieldNames, 
    // Data
    treeDataSimpleMode, treeData, children, loadData, treeLoadedKeys, onTreeLoad, 
    // Expanded
    treeDefaultExpandAll, treeExpandedKeys, treeDefaultExpandedKeys, onTreeExpand, treeExpandAction, 
    // Options
    virtual, listHeight = 200, listItemHeight = 20, onDropdownVisibleChange, dropdownMatchSelectWidth = true, 
    // Tree
    treeLine, treeIcon, showTreeIcon, switcherIcon, treeMotion, 
    // custom
    showSelectAll = true } = props, restProps = __rest(props, ["id", "prefixCls", "value", "defaultValue", "onChange", "onSelect", "onDeselect", "searchValue", "inputValue", "onSearch", "autoClearSearchValue", "filterTreeNode", "treeNodeFilterProp", "showCheckedStrategy", "treeNodeLabelProp", "multiple", "treeCheckable", "treeCheckStrictly", "labelInValue", "fieldNames", "treeDataSimpleMode", "treeData", "children", "loadData", "treeLoadedKeys", "onTreeLoad", "treeDefaultExpandAll", "treeExpandedKeys", "treeDefaultExpandedKeys", "onTreeExpand", "treeExpandAction", "virtual", "listHeight", "listItemHeight", "onDropdownVisibleChange", "dropdownMatchSelectWidth", "treeLine", "treeIcon", "showTreeIcon", "switcherIcon", "treeMotion", "showSelectAll"]);
    const mergedId = useId(id);
    const treeConduction = treeCheckable && !treeCheckStrictly;
    const mergedCheckable = treeCheckable || treeCheckStrictly;
    const mergedLabelInValue = treeCheckStrictly || labelInValue;
    const mergedMultiple = mergedCheckable || multiple;
    // ========================== Warning ===========================
    if (process.env.NODE_ENV !== 'production') {
        warningProps(props);
    }
    // ========================= FieldNames =========================
    const mergedFieldNames = React.useMemo(() => fillFieldNames(fieldNames), 
    /* eslint-disable react-hooks/exhaustive-deps */
    [JSON.stringify(fieldNames)]);
    // =========================== Search ===========================
    const [mergedSearchValue, setSearchValue] = useMergedState('', {
        value: searchValue !== undefined ? searchValue : inputValue,
        postState: search => search || ''
    });
    const onInternalSearch = searchText => {
        setSearchValue(searchText);
        onSearch === null || onSearch === void 0 ? void 0 : onSearch(searchText);
    };
    // ============================ Data ============================
    // `useTreeData` only do convert of `children` or `simpleMode`.
    // Else will return origin `treeData` for perf consideration.
    // Do not do anything to loop the data.
    const mergedTreeData = useTreeData(treeData, children, treeDataSimpleMode);
    const { keyEntities, valueEntities } = useDataEntities(mergedTreeData, mergedFieldNames);
    /** Get `missingRawValues` which not exist in the tree yet */
    const splitRawValues = React.useCallback((newRawValues) => {
        const missingRawValues = [];
        const existRawValues = [];
        // Keep missing value in the cache
        newRawValues.forEach(val => {
            if (valueEntities.has(val)) {
                existRawValues.push(val);
            }
            else {
                missingRawValues.push(val);
            }
        });
        return { missingRawValues, existRawValues };
    }, [valueEntities]);
    // Filtered Tree
    const filteredTreeData = useFilterTreeData(mergedTreeData, mergedSearchValue, {
        fieldNames: mergedFieldNames,
        treeNodeFilterProp,
        filterTreeNode
    });
    // =========================== Label ============================
    const getLabel = React.useCallback((item) => {
        if (item) {
            if (treeNodeLabelProp) {
                return item[treeNodeLabelProp];
            }
            // Loop from fieldNames
            const { _title: titleList } = mergedFieldNames;
            for (let i = 0; i < titleList.length; i += 1) {
                const title = item[titleList[i]];
                if (title !== undefined) {
                    return title;
                }
            }
        }
    }, [mergedFieldNames, treeNodeLabelProp]);
    // ========================= Wrap Value =========================
    const toLabeledValues = React.useCallback((draftValues) => {
        const values = toArray(draftValues);
        return values.map(val => {
            if (isRawValue(val)) {
                return { value: val };
            }
            return val;
        });
    }, []);
    const convert2LabelValues = React.useCallback((draftValues) => {
        const values = toLabeledValues(draftValues);
        return values.map(item => {
            let { label: rawLabel } = item;
            const { value: rawValue, halfChecked: rawHalfChecked } = item;
            let rawDisabled;
            const entity = valueEntities.get(rawValue);
            // Fill missing label & status
            if (entity) {
                rawLabel = rawLabel !== null && rawLabel !== void 0 ? rawLabel : getLabel(entity.node);
                rawDisabled = entity.node.disabled;
            }
            return {
                label: rawLabel,
                value: rawValue,
                halfChecked: rawHalfChecked,
                disabled: rawDisabled
            };
        });
    }, [valueEntities, getLabel, toLabeledValues]);
    // =========================== Values ===========================
    const [internalValue, setInternalValue] = useMergedState(defaultValue, { value });
    const rawMixedLabeledValues = React.useMemo(() => toLabeledValues(internalValue), [
        toLabeledValues,
        internalValue
    ]);
    // Split value into full check and half check
    const [rawLabeledValues, rawHalfLabeledValues] = React.useMemo(() => {
        const fullCheckValues = [];
        const halfCheckValues = [];
        rawMixedLabeledValues.forEach(item => {
            if (item.halfChecked) {
                halfCheckValues.push(item);
            }
            else {
                fullCheckValues.push(item);
            }
        });
        return [fullCheckValues, halfCheckValues];
    }, [rawMixedLabeledValues]);
    // const [mergedValues] = useCache(rawLabeledValues);
    const rawValues = React.useMemo(() => rawLabeledValues.map(item => item.value), [
        rawLabeledValues
    ]);
    // Convert value to key. Will fill missed keys for conduct check.
    const [rawCheckedValues, rawHalfCheckedValues] = useCheckedKeys(rawLabeledValues, rawHalfLabeledValues, treeConduction, keyEntities);
    // Convert rawCheckedKeys to check strategy related values
    const displayValues = React.useMemo(() => {
        // Collect keys which need to show
        const displayKeys = formatStrategyValues(rawCheckedValues, showCheckedStrategy, keyEntities, mergedFieldNames);
        // Convert to value and filled with label
        const values = displayKeys.map(key => { var _a, _b, _c; return (_c = (_b = (_a = keyEntities[key]) === null || _a === void 0 ? void 0 : _a.node) === null || _b === void 0 ? void 0 : _b[mergedFieldNames.value]) !== null && _c !== void 0 ? _c : key; });
        // Back fill with origin label
        const labeledValues = values.map(val => {
            const targetItem = rawLabeledValues.find(item => item.value === val);
            return {
                value: val,
                label: targetItem === null || targetItem === void 0 ? void 0 : targetItem.label
            };
        });
        const rawDisplayValues = convert2LabelValues(labeledValues);
        const firstVal = rawDisplayValues[0];
        if (!mergedMultiple && firstVal && isNil(firstVal.value) && isNil(firstVal.label)) {
            return [];
        }
        return rawDisplayValues.map(item => {
            var _a;
            return (Object.assign(Object.assign({}, item), { label: (_a = item.label) !== null && _a !== void 0 ? _a : item.value }));
        });
    }, [
        mergedFieldNames,
        mergedMultiple,
        rawCheckedValues,
        rawLabeledValues,
        convert2LabelValues,
        showCheckedStrategy,
        keyEntities
    ]);
    const [cachedDisplayValues] = useCache(displayValues);
    // =========================== Change ===========================
    const triggerChange = useRefFunc((newRawValues, extra, source) => {
        const labeledValues = convert2LabelValues(newRawValues);
        setInternalValue(labeledValues);
        // Clean up if needed
        if (autoClearSearchValue) {
            setSearchValue('');
        }
        // Generate rest parameters is costly, so only do it when necessary
        if (onChange) {
            let eventValues = newRawValues;
            if (treeConduction) {
                const formattedKeyList = formatStrategyValues(newRawValues, showCheckedStrategy, keyEntities, mergedFieldNames);
                eventValues = formattedKeyList.map(key => {
                    const entity = valueEntities.get(key);
                    return entity ? entity.node[mergedFieldNames.value] : key;
                });
            }
            const { triggerValue, selected } = extra || {
                triggerValue: undefined,
                selected: undefined
            };
            let returnRawValues = eventValues;
            // We need fill half check back
            if (treeCheckStrictly) {
                const halfValues = rawHalfLabeledValues.filter(item => !eventValues.includes(item.value));
                returnRawValues = [...returnRawValues, ...halfValues];
            }
            const returnLabeledValues = convert2LabelValues(returnRawValues);
            const additionalInfo = {
                // [Legacy] Always return as array contains label & value
                preValue: rawLabeledValues,
                triggerValue
            };
            // [Legacy] Fill legacy data if user query.
            // This is expansive that we only fill when user query
            // https://github.com/react-component/tree-select/blob/fe33eb7c27830c9ac70cd1fdb1ebbe7bc679c16a/src/Select.jsx
            let showPosition = true;
            if (treeCheckStrictly || (source === 'selection' && !selected)) {
                showPosition = false;
            }
            fillAdditionalInfo(additionalInfo, triggerValue, newRawValues, mergedTreeData, showPosition, mergedFieldNames);
            if (mergedCheckable) {
                additionalInfo.checked = selected;
            }
            else {
                additionalInfo.selected = selected;
            }
            const returnValues = mergedLabelInValue
                ? returnLabeledValues
                : returnLabeledValues.map(item => item.value);
            onChange(mergedMultiple ? returnValues : returnValues[0], mergedLabelInValue ? null : returnLabeledValues.map(item => item.label), additionalInfo);
        }
    });
    // ========================== Options ===========================
    /** Trigger by option list */
    const onOptionSelect = React.useCallback((selectedKey, { selected, source }) => {
        var _a;
        const entity = keyEntities[selectedKey];
        const node = entity === null || entity === void 0 ? void 0 : entity.node;
        const selectedValue = (_a = node === null || node === void 0 ? void 0 : node[mergedFieldNames.value]) !== null && _a !== void 0 ? _a : selectedKey;
        // Never be falsy but keep it safe
        if (!mergedMultiple) {
            // Single mode always set value
            triggerChange([selectedValue], { selected: true, triggerValue: selectedValue }, 'option');
        }
        else {
            let newRawValues = selected
                ? [...rawValues, selectedValue]
                : rawCheckedValues.filter(v => v !== selectedValue);
            // Add keys if tree conduction
            if (treeConduction) {
                // Should keep missing values
                const { missingRawValues, existRawValues } = splitRawValues(newRawValues);
                const keyList = existRawValues.map(val => valueEntities.get(val).key);
                // Conduction by selected or not
                let checkedKeys;
                if (selected) {
                    ({ checkedKeys } = conductCheck(keyList, true, keyEntities));
                }
                else {
                    ({ checkedKeys } = conductCheck(keyList, { checked: false, halfCheckedKeys: rawHalfCheckedValues }, keyEntities));
                }
                // Fill back of keys
                newRawValues = [
                    ...missingRawValues,
                    ...checkedKeys.map(key => keyEntities[key].node[mergedFieldNames.value])
                ];
            }
            triggerChange(newRawValues, { selected, triggerValue: selectedValue }, source || 'option');
        }
        // Trigger select event
        if (selected || !mergedMultiple) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(selectedValue, fillLegacyProps(node));
        }
        else {
            onDeselect === null || onDeselect === void 0 ? void 0 : onDeselect(selectedValue, fillLegacyProps(node));
        }
    }, [
        splitRawValues,
        valueEntities,
        keyEntities,
        mergedFieldNames,
        mergedMultiple,
        rawValues,
        triggerChange,
        treeConduction,
        onSelect,
        onDeselect,
        rawCheckedValues,
        rawHalfCheckedValues
    ]);
    // ========================== Dropdown ==========================
    const onInternalDropdownVisibleChange = React.useCallback((open) => {
        if (onDropdownVisibleChange) {
            const legacyParam = {};
            Object.defineProperty(legacyParam, 'documentClickClose', {
                get() {
                    warning(false, 'Second param of `onDropdownVisibleChange` has been removed.');
                    return false;
                }
            });
            onDropdownVisibleChange(open, legacyParam);
        }
    }, [onDropdownVisibleChange]);
    // ====================== Display Change ========================
    const onDisplayValuesChange = useRefFunc((newValues, info) => {
        const newRawValues = newValues.map(item => item.value);
        if (info.type === 'clear') {
            triggerChange(newRawValues, {}, 'selection');
            return;
        }
        // TreeSelect only have multiple mode which means display change only has remove
        if (info.values.length) {
            onOptionSelect(info.values[0].value, { selected: false, source: 'selection' });
        }
    });
    // ========================== Context ===========================
    const treeSelectContext = React.useMemo(() => ({
        virtual,
        dropdownMatchSelectWidth,
        listHeight,
        listItemHeight,
        treeData: filteredTreeData,
        fieldNames: mergedFieldNames,
        onSelect: onOptionSelect,
        triggerChange,
        treeExpandAction
    }), [
        virtual,
        dropdownMatchSelectWidth,
        listHeight,
        listItemHeight,
        filteredTreeData,
        mergedFieldNames,
        onOptionSelect,
        treeExpandAction
    ]);
    // ======================= Legacy Context =======================
    const legacyContext = React.useMemo(() => ({
        checkable: mergedCheckable,
        loadData,
        treeLoadedKeys,
        onTreeLoad,
        checkedKeys: rawCheckedValues,
        halfCheckedKeys: rawHalfCheckedValues,
        treeDefaultExpandAll,
        treeExpandedKeys,
        treeDefaultExpandedKeys,
        onTreeExpand,
        treeIcon,
        treeMotion,
        showTreeIcon,
        showSelectAll,
        switcherIcon,
        treeLine,
        treeNodeFilterProp,
        keyEntities
    }), [
        mergedCheckable,
        loadData,
        treeLoadedKeys,
        onTreeLoad,
        rawCheckedValues,
        rawHalfCheckedValues,
        treeDefaultExpandAll,
        treeExpandedKeys,
        treeDefaultExpandedKeys,
        onTreeExpand,
        treeIcon,
        treeMotion,
        showTreeIcon,
        showSelectAll,
        switcherIcon,
        treeLine,
        treeNodeFilterProp,
        keyEntities
    ]);
    // =========================== Render ===========================
    return (<TreeSelectContext.Provider value={treeSelectContext}>
            <LegacyContext.Provider value={legacyContext}>
                <BaseSelect ref={ref} {...restProps} 
    // >>> MISC
    id={mergedId} prefixCls={prefixCls} mode={mergedMultiple ? 'multiple' : undefined} 
    // >>> Display Value
    displayValues={cachedDisplayValues} onDisplayValuesChange={onDisplayValuesChange} 
    // >>> Search
    searchValue={mergedSearchValue} onSearch={onInternalSearch} 
    // >>> Options
    OptionList={OptionList} emptyOptions={!mergedTreeData.length} onDropdownVisibleChange={onInternalDropdownVisibleChange} dropdownMatchSelectWidth={dropdownMatchSelectWidth}/>
            </LegacyContext.Provider>
        </TreeSelectContext.Provider>);
});
// Assign name for Debug
if (process.env.NODE_ENV !== 'production') {
    TreeSelect.displayName = 'TreeSelect';
}
const GenericTreeSelect = TreeSelect;
GenericTreeSelect.TreeNode = TreeNode;
GenericTreeSelect.SHOW_ALL = SHOW_ALL;
GenericTreeSelect.SHOW_PARENT = SHOW_PARENT;
GenericTreeSelect.SHOW_CHILD = SHOW_CHILD;
export default GenericTreeSelect;
